/* Realistic 3D layer for the assembly guide (sections/htn-assembly.liquid).
   Listens for 'asm:step' from htn-assembly.js and animates lit, shaded models of the parts.
   Parts follow published sizes where they exist (MATE manual, Rusan catalogue, Arken spec table); see each part's comment.
   Sizes nobody publishes (connector, ocular, clamp collar) are taken from product photos and are approximate.
   Without WebGL, or if three.js fails to load, the SVG drawing stays in place. */
(function () {
  'use strict';
  var BASE = 'https://cdn.jsdelivr.net/npm/three@0.147.0/';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  var libs = null;

  function load(src) {
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = src; s.async = true; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }
  function getLibs() {
    if (!libs) {
      libs = (window.THREE ? Promise.resolve() : load(BASE + 'build/three.min.js')).then(function () {
        return Promise.all([
          'examples/js/environments/RoomEnvironment.js',
          'examples/js/controls/OrbitControls.js',
          'examples/js/geometries/RoundedBoxGeometry.js'
        ].map(function (p) { return load(BASE + p); }));
      });
    }
    return libs;
  }
  function hasWebGL() {
    try { var c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { return false; }
  }

  /* ---------- geometry helpers (all parts share one optical axis along +x, units ≈ mm) ---------- */
  var T, M;

  function chamfer(pts, c) {
    var out = [], n = pts.length;
    for (var i = 0; i < n; i++) {
      var p = pts[i];
      if (i === 0 || i === n - 1 || p[1] === 0) { out.push(p); continue; }
      var a = pts[i - 1], b = pts[i + 1];
      var da = Math.hypot(a[0] - p[0], a[1] - p[1]), db = Math.hypot(b[0] - p[0], b[1] - p[1]);
      var ca = Math.min(c, da / 3), cb = Math.min(c, db / 3);
      out.push([p[0] + (a[0] - p[0]) * ca / da, p[1] + (a[1] - p[1]) * ca / da]);
      out.push([p[0] + (b[0] - p[0]) * cb / db, p[1] + (b[1] - p[1]) * cb / db]);
    }
    return out;
  }
  // pts: [[x, r], ...] traced front-to-back along the outside; closed loops repeat the first point.
  function lathe(pts, mat, opt) {
    opt = opt || {};
    var p = chamfer(pts, opt.c === undefined ? 0.6 : opt.c);
    var g = new T.LatheGeometry(p.map(function (q) { return new T.Vector2(q[1], q[0]); }), opt.seg || 96);
    g.rotateZ(-Math.PI / 2);
    var mesh = new T.Mesh(g, mat);
    mesh.castShadow = true; mesh.receiveShadow = true;
    return mesh;
  }
  function ring(x0, x1, ri, ro, mat, c) {
    return lathe([[x0, ri], [x0, ro], [x1, ro], [x1, ri], [x0, ri]], mat, { c: c });
  }
  function knurl(r, len, ridges, mat, depth) {
    var g = new T.CylinderGeometry(r, r, len, ridges * 2, 1, false);
    var pos = g.attributes.position, d = depth || 0.045;
    for (var i = 0; i < pos.count; i++) {
      var x = pos.getX(i), z = pos.getZ(i), rr = Math.hypot(x, z);
      if (rr < 1e-3) continue;
      var a = Math.atan2(z, x), k = Math.round(a / (Math.PI / ridges));
      if (k % 2) { pos.setX(i, x * (1 - d)); pos.setZ(i, z * (1 - d)); }
    }
    g.computeVertexNormals();
    g.rotateZ(-Math.PI / 2);
    var m = new T.Mesh(g, mat); m.castShadow = true; m.receiveShadow = true;
    return m;
  }
  function thread(x0, x1, ri, ro, pitch, mat) {
    var pts = [[x0, ri], [x0, ro - 0.5]];
    for (var x = x0 + pitch / 2, k = 0; x < x1; x += pitch / 2, k++) pts.push([x, k % 2 ? ro - 0.55 : ro]);
    pts.push([x1, ro - 0.5], [x1, ri], [x0, ri]);
    return lathe(pts, mat, { c: 0.05, seg: 96 });
  }
  function lens(x, r, bulge, dir, mat) {
    var pts = [], n = 12;
    for (var i = 0; i <= n; i++) {
      var rr = r * i / n;
      pts.push([x - dir * bulge * (1 - (rr / r) * (rr / r)), rr]);
    }
    pts.push([x + dir * 2, r], [x + dir * 2, 0]);
    if (dir < 0) pts.reverse();
    return lathe(dir > 0 ? pts : pts, mat, { c: 0, seg: 64 });
  }
  function rbox(w, h, d, r, mat) {
    var m = new T.Mesh(new T.RoundedBoxGeometry(w, h, d, 4, r), mat);
    m.castShadow = true; m.receiveShadow = true;
    return m;
  }
  function at(o, x, y, z) { o.position.set(x || 0, y || 0, z || 0); return o; }

  function textTex(draw, w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d');
    draw(ctx, w, h);
    var t = new T.CanvasTexture(c);
    t.encoding = T.sRGBEncoding;
    t.anisotropy = 4;
    return t;
  }
  function decalMat(tex) {
    return new T.MeshStandardMaterial({ map: tex, transparent: true, roughness: 0.55, metalness: 0.1, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 });
  }
  // Curved decal wrapped on a cylinder of radius r, centred on angle `face` (0 = +z, -PI/2 = top).
  function wrapDecal(r, x0, x1, arc, face, tex) {
    var g = new T.CylinderGeometry(r, r, x1 - x0, 48, 1, true, face - arc / 2, arc);
    g.rotateZ(-Math.PI / 2);
    g.translate((x0 + x1) / 2, 0, 0);
    return new T.Mesh(g, decalMat(tex));
  }

  /* ---------- parts ---------- */
  // Envelope to the published MATE H50R size (manual §4: 120 x 92 x 76 mm); detail inside it follows the manual's §5 photos.
  function partMate() {
    var g = new T.Group();
    // objective housing (x -120..-88), lens and flip-up lens cover
    g.add(lathe([[-120, 27], [-120, 34], [-116, 34], [-114, 33], [-90, 33], [-88, 31], [-88, 27], [-120, 27]], M.body));
    g.add(lens(-113, 27, 3, 1, M.glass));
    var cap = new T.Group();
    cap.add(lathe([[-5, 0], [-5, 34.5], [-1.2, 35], [0, 33.5], [0, 0]], M.matte));
    cap.add(at(rbox(1.6, 14, 14, 1, M.rubber), -5.6, 0, 0));
    var hinge = new T.Group();
    cap.position.set(-1, -36, 0);
    hinge.add(cap);
    hinge.add(at(rbox(8, 6, 16, 2, M.body), 2, 0, 0));
    hinge.position.set(-118, 37, 0);
    hinge.rotation.z = T.MathUtils.degToRad(-150);
    g.add(hinge);
    // laser rangefinder block on the left flank, beside the objective
    g.add(at(rbox(26, 24, 12, 3, M.body), -106, -4, -38));
    [[4], [-6]].forEach(function (o) {
      var w = new T.Mesh(new T.CircleGeometry(3, 24), M.glass);
      w.rotation.y = -Math.PI / 2; w.position.set(-119.2, -4 + o[0], -38);
      g.add(w);
    });
    // main body (y -33..30) and battery tube on top (to y 43, overall height 76)
    g.add(at(rbox(72, 63, 60, 12, M.body), -60, -1.5, 0));
    g.add(at(rbox(80, 17, 34, 7, M.body), -60, 34.5, 0));
    for (var i = 0; i < 8; i++) g.add(at(rbox(1.2, 12, 1.6, 0.5, M.anod), -93 + i * 3, 12, 30.2));
    var dial = knurl(6, 5, 18, M.knurl, 0.06); dial.rotation.z = Math.PI / 2; at(dial, -36, 44.5, 0);
    g.add(dial);
    // rear housing, adapter locked ring, M52x0.75 thread (Ø52) and eyepiece glass
    g.add(lathe([[-26, 0], [-26, 31.5], [-16, 31.5], [-12, 30], [-12, 0]], M.body));
    g.add(at(knurl(29.5, 7, 60, M.knurl, 0.035), -8.5, 0, 0));
    g.add(thread(-5, 0, 22, 26, 0.75, M.thread));
    g.add(lens(-2.5, 21, 1.8, -1, M.glass));
    // focus knob (tip at z +48, overall width 92 with the rangefinder), buttons and power key
    var knob = new T.Group();
    var kn = knurl(11, 12, 30, M.knurl, 0.06); kn.rotation.y = -Math.PI / 2; at(kn, 0, 0, 8);
    knob.add(kn);
    var kc = lathe([[0, 0], [0, 9], [1.5, 8.5], [1.5, 0]], M.anod); kc.rotation.y = -Math.PI / 2; at(kc, 0, 0, 14);
    knob.add(kc);
    knob.position.set(-74, -14, 30);
    g.add(knob);
    [[-46, 10], [-34, 10], [-40, -4]].forEach(function (b) { g.add(at(rbox(10, 10, 4, 2, M.rubber), b[0], b[1], 30.5)); });
    var pw = lathe([[0, 0], [0, 4.5], [2, 4], [2, 0]], M.rubber); pw.rotation.y = -Math.PI / 2; at(pw, -58, -2, 30);
    g.add(pw);
    var name = textTex(function (c, w, h) {
      c.font = '700 54px Arial, sans-serif'; c.textBaseline = 'middle';
      c.fillStyle = '#d9d9d6'; c.fillText('MATE', 10, h / 2);
    }, 256, 64);
    var nm = new T.Mesh(new T.PlaneGeometry(20, 5), decalMat(name));
    at(nm, -66, 22, 30.1);
    g.add(nm);
    return { group: g };
  }

  // Shared clip-on pieces. Every clip-on sits with its M52x0.75 thread ending at x 0, like the MATE.
  function flipCap(r, x, hingeZ) {
    // lens cover swung open on a side hinge at +z (the side the camera sees)
    var hinge = new T.Group();
    var cap = new T.Group();
    cap.add(lathe([[-4, 0], [-4, r + 0.5], [-1, r + 1], [0, r - 0.5], [0, 0]], M.matte));
    cap.position.set(0, 0, -r - 1);
    hinge.add(cap);
    hinge.add(at(rbox(10, 14, 7, 2.5, M.body), 0, 0, 1));
    hinge.position.set(x, 0, hingeZ);
    hinge.rotation.y = T.MathUtils.degToRad(-115);
    return hinge;
  }
  function plate(text, w, h, size, color) {
    var t = textTex(function (c, cw, ch) {
      c.font = '700 ' + size + 'px Arial, sans-serif'; c.textBaseline = 'middle'; c.textAlign = 'center';
      c.fillStyle = color || '#d9d9d6'; c.fillText(text, cw / 2, ch / 2);
    }, 512, 128);
    return new T.Mesh(new T.PlaneGeometry(w, h), decalMat(t));
  }
  function lrfWindows(x, y, gap) {
    var g = new T.Group();
    g.add(at(rbox(1.5, 9, 20, 2, M.dark), x + 0.5, y, 0));
    [[-gap / 2, 3.4], [gap / 2, 2.6]].forEach(function (o) {
      var w = new T.Mesh(new T.CircleGeometry(o[1], 24), M.glass);
      w.rotation.y = -Math.PI / 2; w.position.set(x - 0.3, y, o[0]);
      g.add(w);
    });
    return g;
  }

  // HIKMICRO Thunder 3.0 (datasheets: TH35C 143.4 x 67.5 x 72.9 mm, centre height 32; TQ50CL 150.3 x 83 x 78.1 mm, centre height 35.5;
  // TQ35CL 152.3 x 63 x 74.1 mm). Rear M52x0.75 thread with a wrench-tightened fixing ring (manual 2.4). Section lengths, red objective ring,
  // focus knob, top buttons, side battery door and flip cover follow the HIKMICRO product photos.
  function partThunder(o) {
    var g = new T.Group(), L = o.L, R = o.objR, top = o.top, bot = o.bottom;
    var x0 = -L, xo = -L + o.objLen;            // objective housing x0..xo
    var xb = xo + (o.flare ? 18 : 10);          // body starts
    var xr = -17;                               // body ends, rear housing begins
    if (o.flare) {
      g.add(lathe([[x0, R - 6], [x0, R], [xo, R], [xo + 18, o.neckR], [xb + 2, o.neckR], [xb + 2, 0]], M.body, { c: 1.2 }));
    } else {
      g.add(lathe([[x0, R - 5], [x0, R], [xo, R], [xo + 2, R - 2], [xo + 2, o.neckR], [xb + 2, o.neckR], [xb + 2, 0]], M.body, { c: 1 }));
    }
    g.add(ring(xo - 4, xo - 1.6, R - 0.2, R + 0.25, M.red, 0.2));
    g.add(lathe([[x0 + 1, 0], [x0 + 1, R - 4], [x0 + 6, R - 4], [x0 + 6, 0]], M.matte));
    g.add(lens(x0 + 5, o.lensR, 2.4, 1, M.glass));
    if (o.lrf) g.add(lrfWindows(x0 + 4.5, o.lensR + 4.5, 9));
    g.add(flipCap(R, x0 - 2, R + 3));
    // main body: rounded slab, flat top carrying the buttons, flat bottom at the published centre height
    var bh = top - bot, bl = xr - xb;
    g.add(at(rbox(bl, bh, o.W - 4, 13, M.body), xb + bl / 2, bot + bh / 2, 0));
    [-1, 0, 1].forEach(function (i) { g.add(at(rbox(9, 3, 7, 1.4, M.rubber), xb + bl * 0.58 + i * 13, top + 1, 0)); });
    // focus knob on a boss just behind the objective
    g.add(at(rbox(18, 10, 22, 4, M.body), xb + 4, top - 2, 0));
    var kn = knurl(9.5, 10, 26, M.knurl, 0.07); kn.rotation.z = Math.PI / 2; at(kn, xb + 4, top + 8, 0); g.add(kn);
    var kc = lathe([[0, 0], [0, 8.8], [1.5, 8.2], [1.5, 0]], M.anod); kc.rotation.z = Math.PI / 2; at(kc, xb + 4, top + 13, 0); g.add(kc);
    // battery door with the model label on the right side (-z), HIKMICRO badge and port covers on the left (+z)
    g.add(at(rbox(bl * 0.62, bh * 0.62, 2, 2, M.matte), xb + bl * 0.55, bot + bh * 0.45, -(o.W - 4) / 2));
    var md = plate(o.model, 24, 6, 60); md.rotation.y = Math.PI; at(md, xb + bl * 0.55, bot + bh * 0.55, -(o.W - 4) / 2 - 1.1); g.add(md);
    g.add(at(rbox(30, 9, 1.5, 1.5, M.matte), xb + bl * 0.5, bot + bh * 0.55, (o.W - 4) / 2));
    var bd = plate('HIKMICRO', 26, 5, 64); at(bd, xb + bl * 0.5, bot + bh * 0.55, (o.W - 4) / 2 + 0.8); g.add(bd);
    [0, 1].forEach(function (i) { g.add(at(rbox(9, 5, 1.6, 1.2, M.rubber), xb + bl * 0.42 + i * 12, bot + 7, (o.W - 4) / 2)); });
    // rear housing, wrench fixing ring and the exposed M52x0.75 thread
    g.add(lathe([[xr - 2, 0], [xr - 2, 29], [-13, 29], [-13, 0]], M.body));
    g.add(at(knurl(27.6, 3, 40, M.anod, 0.03), -11.5, 0, 0));
    g.add(thread(-10, 0, 22, 26, 0.75, M.thread));
    g.add(lens(-2.5, 20, 1.6, -1, M.glass));
    return { group: g };
  }

  // ThermTec Hunt Pro series (ThermTec spec tables: Hunt 650 Pro 124 x 70 x 77 mm, Hunt 335 Pro 120 x 66 x 77 mm, Hunt 650L 137 x 82 x 86 mm).
  // Rear: M52x0.75 connection thread with the eyepiece bayonet-type ring and its latch ahead of it (manual section 5).
  // Cylinder body, orange objective ring, focus knob, three top keys (orange centre), foot and hinged cover follow the ThermTec product photos.
  function partHunt(o) {
    var g = new T.Group(), L = o.L, R = o.R, x0 = -L;
    if (o.lrf) {
      g.add(lathe([[x0, o.objR - 6], [x0, o.objR], [x0 + 30, o.objR], [x0 + 42, R], [-14, R], [-14, 0]], M.body, { c: 1.4 }));
      g.add(lrfWindows(x0 + 4.5, o.lensR + 6.5, 10));
    } else {
      g.add(lathe([[x0, R - 4], [x0, o.objR], [x0 + 14, o.objR], [x0 + 16, R], [-14, R], [-14, 0]], M.body, { c: 1.2 }));
      g.add(ring(x0 + 9, x0 + 11.5, o.objR - 0.2, o.objR + 0.25, M.orange, 0.2));
      g.add(flipCap(o.objR, x0 - 2, o.objR + 3));
    }
    g.add(lathe([[x0 + 1, 0], [x0 + 1, o.objR - 4], [x0 + 6, o.objR - 4], [x0 + 6, 0]], M.matte));
    g.add(lens(x0 + 5, o.lensR, 2.4, 1, M.glass));
    // flat top panel with three keys (orange in the middle) behind the focus knob
    var kx = x0 + (o.lrf ? 50 : 30), px = kx + 26;
    g.add(at(rbox(44, 6, 24, 3, M.body), px, R - 1.5, 0));
    g.add(at(rbox(9, 3, 9, 2, M.rubber), px - 13, R + 2, 0));
    g.add(at(rbox(9, 3.4, 9, 2, M.orange), px, R + 2, 0));
    g.add(at(rbox(9, 3, 9, 2, M.rubber), px + 13, R + 2, 0));
    g.add(at(rbox(18, 8, 20, 4, M.body), kx, R - 1, 0));
    var kn = knurl(10, 11, 24, M.knurl, 0.07); kn.rotation.z = Math.PI / 2; at(kn, kx, o.top - 7.5, 0); g.add(kn);
    var kc = lathe([[0, 0], [0, 9.4], [2, 8.8], [2, 0]], M.anod); kc.rotation.z = Math.PI / 2; at(kc, kx, o.top - 2, 0); g.add(kc);
    g.add(at(new T.Mesh(new T.TorusGeometry(7.6, 0.45, 8, 40), M.orange), kx, o.top + 0.05, 0).rotateX(Math.PI / 2));
    // foot under the body, side panels (ThermTec logo on the right, port panel on the left)
    g.add(at(rbox(42, o.foot - R + 4, 30, 2, M.body), x0 + L * 0.48, -(o.foot + R - 4) / 2, 0));
    g.add(at(rbox(34, 20, 2, 6, M.matte), x0 + L * 0.58, -2, R - 0.6));
    var lg = plate('ThermTec', 26, 6.5, 74, '#e9e9e5'); lg.rotation.y = Math.PI; at(lg, x0 + L * 0.5, -4, -R - 0.2); g.add(lg);
    // rear: bayonet-type ring with its latch, then the M52x0.75 thread
    g.add(ring(-14, -9, 25, 28.5, M.anod, 0.6));
    g.add(at(rbox(5, 7, 9, 1.2, M.anod), -11.5, 31, 0));
    g.add(thread(-9, 0, 22, 26, 0.75, M.thread));
    g.add(lens(-2.5, 20, 1.6, -1, M.glass));
    return { group: g };
  }

  // ZEISS DTC 3/38: 164 x 60 x 65 mm (ZEISS manual). Straight tube, focus turret on top near the front,
  // boxier rear housing with the 4-key cross on top, ribbed lock ring over the M52 thread. Lens radius is inferred from 38 mm f/1.0.
  function partZeiss(o) {
    var g = new T.Group(), L = o.L, R = o.R, x0 = -L;
    g.add(lathe([[x0, R - 5], [x0, R], [-16, R], [-16, 0]], M.body, { c: 1.2 }));
    g.add(at(knurl(R + 0.6, 16, 48, M.rubber, 0.04), x0 + 14, 0, 0));
    g.add(lathe([[x0 + 1, 0], [x0 + 1, R - 5], [x0 + 5, R - 5], [x0 + 5, 0]], M.matte));
    g.add(lens(x0 + 4.5, o.lensR, 2.2, 1, M.glass));
    g.add(flipCap(R, x0 - 2, R + 3));
    // rear housing (flat top, deeper flanks) with the cross of four keys
    var hx0 = x0 + 48, hL = -16 - hx0;
    g.add(at(rbox(hL, 2 * R + 3, 2 * R + 2, 6, M.body), hx0 + hL / 2, 1.5, 0));
    var cx = hx0 + hL * 0.6, cy = R + 3.4;
    g.add(at(rbox(8, 2.4, 8, 2, M.rubber), cx, cy, 0));
    g.add(at(rbox(7, 2.2, 6, 2, M.rubber), cx - 9, cy, 0));
    g.add(at(rbox(7, 2.2, 6, 2, M.rubber), cx + 9, cy, 0));
    g.add(at(rbox(6, 2.2, 7, 2, M.rubber), cx, cy, 9));
    g.add(at(rbox(6, 2.2, 7, 2, M.rubber), cx, cy, -9));
    // focus turret on top just behind the objective
    var tx = x0 + 36;
    g.add(at(rbox(16, 6, 16, 3, M.body), tx, R + 1, 0));
    var kn = knurl(7.5, 9, 20, M.knurl, 0.07); kn.rotation.z = Math.PI / 2; at(kn, tx, o.top - 5, 0); g.add(kn);
    var lg = plate('ZEISS', 20, 6, 80, '#7fa6d8'); at(lg, hx0 + hL * 0.45, 0, R + 1.05); g.add(lg);
    // rear: ribbed lock ring, then the M52 thread
    g.add(at(knurl(27.5, 7, 40, M.rubber, 0.05), -12.5, 0, 0));
    g.add(thread(-9, 0, 22, 26, 0.75, M.thread));
    g.add(lens(-2.5, 20, 1.6, -1, M.glass));
    return { group: g };
  }

  function arrowTex() {
    return textTex(function (c, w, h) {
      c.fillStyle = '#f1f1ee';
      c.beginPath(); c.moveTo(w / 2, h * 0.15); c.lineTo(w * 0.8, h * 0.85); c.lineTo(w * 0.2, h * 0.85); c.closePath(); c.fill();
    }, 64, 64);
  }

  function partConnector() {
    var g = new T.Group();
    g.add(lathe([[-6, 26.2], [-6, 29.5], [1.5, 29.5], [2.2, 28.6], [3.6, 28.6], [4.3, 31.5], [10, 31.5], [10, 27.5], [4, 27.5], [4, 26.2], [-6, 26.2]], M.anod, { c: 0.4 }));
    for (var i = 0; i < 4; i++) {
      var a = Math.PI / 4 + i * Math.PI / 2;
      var lug = rbox(5, 3.2, 9, 0.8, M.anod);
      var piv = new T.Group(); piv.rotation.x = a; lug.position.set(7, 32.8, 0); piv.add(lug);
      g.add(piv);
    }
    g.add(wrapDecal(31.55, 5.5, 9, 0.22, -Math.PI / 2, arrowTex()));
    return { group: g };
  }

  // MAR clamp sized to the SH4 bell (≈59 mm; Rusan lists MAR-58, -58.4 and -59), clamping length 37 mm (Rusan catalogue p.5).
  // The front locking ring carries the UNLOCK arrow and turns to lock; Rusan: "no need for rotation of clip-on device when locking it".
  // Collar and lever sized from the store photo.
  var LOCK_OPEN = -30;
  function partClamp() {
    var g = new T.Group();
    var collar = new T.Group();
    collar.add(ring(4, 16, 31.8, 35.8, M.anod, 0.7));
    var tab = new T.Group(); tab.rotation.x = T.MathUtils.degToRad(130);
    tab.add(at(rbox(9, 8, 9, 2, M.anod), 10, 37.8, 0)); collar.add(tab);
    var unlock = textTex(function (c, w, h) {
      c.fillStyle = '#ecece8'; c.font = '700 46px Arial, sans-serif'; c.textBaseline = 'middle';
      c.fillText('UNLOCK', 70, h / 2);
      c.lineWidth = 6; c.strokeStyle = '#ecece8';
      c.beginPath(); c.moveTo(12, h / 2); c.lineTo(58, h / 2); c.stroke();
      c.beginPath(); c.moveTo(4, h / 2); c.lineTo(22, h / 2 - 12); c.lineTo(22, h / 2 + 12); c.closePath(); c.fill();
    }, 320, 64);
    collar.add(wrapDecal(35.85, 6.5, 13.5, 0.66, -0.75, unlock));
    // alignment arrow sits on top when the ring is open, so it meets the connector's arrow
    collar.add(wrapDecal(35.85, 6.5, 13.5, 0.12, -Math.PI / 2 - T.MathUtils.degToRad(LOCK_OPEN), arrowTex()));
    g.add(collar);
    g.add(ring(16, 53, 30.2, 35, M.anod, 0.7));
    g.add(ring(17, 52, 29.5, 30.25, M.liner, 0.1));
    g.add(at(rbox(32, 11, 20, 2.5, M.anod), 37, 38, 0));
    [[28, -6], [38, -6], [38, 8]].forEach(function (h) {
      var d = new T.Mesh(new T.CircleGeometry(1.6, 20), M.dark);
      var piv = new T.Group(); piv.rotation.x = -0.35;
      d.position.set(h[0], h[1], 35.05); piv.add(d); g.add(piv);
    });
    var lever = new T.Group();
    lever.add(at(rbox(54, 3.5, 8, 1.6, M.anod), -25, 0, 0));
    lever.add(at(rbox(9, 5.5, 10, 2.4, M.anod), -50, 1, 0));
    lever.position.set(49, 45, 0);
    g.add(lever);
    return { group: g, lever: lever, collar: collar };
  }

  // Arken SH4 4-16x50 GEN2. Published: length 13.4 in (340 mm), 34 mm tube, 50 mm objective, 35.5 mm ocular (arkenopticsusa.com spec table).
  // Section lengths, bell Ø (≈59 mm) and turret heights are scaled from Arken's side-profile render (SH4-416-03) against the 340 mm length.
  // Objective face at x 17, eyepiece end at x 357; +z is the shooter's left (parallax/illumination side).
  function sideTex(lines, w, h, size) {
    return textTex(function (c, cw, ch) {
      c.save(); c.translate(cw / 2, ch / 2); c.rotate(-Math.PI / 2);
      c.fillStyle = '#d6d6d2'; c.textAlign = 'center'; c.textBaseline = 'middle';
      lines.forEach(function (l, i) { c.font = (l[1] || 500) + ' ' + size + 'px Arial, sans-serif'; c.fillText(l[0], 0, (i - (lines.length - 1) / 2) * size * 1.15); });
      c.restore();
    }, w, h);
  }
  // Dial band wrapped all the way round: tick marks plus numbers, upright along the knob axis.
  function dialTex(labels, ticks) {
    return textTex(function (c, w, h) {
      c.fillStyle = '#e4e4e0'; c.strokeStyle = '#e4e4e0'; c.lineWidth = 3;
      for (var i = 0; i < ticks; i++) {
        var x = (i + 0.5) * w / ticks, big = i % (ticks / labels.length) === 0;
        c.beginPath(); c.moveTo(x, 0); c.lineTo(x, big ? h * 0.42 : h * 0.24); c.stroke();
      }
      c.font = '600 ' + Math.round(h * 0.44) + 'px Arial, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
      labels.forEach(function (l, i) { c.fillText(l, (i * ticks / labels.length + 0.5) * w / ticks, h * 0.94); });
    }, 1024, 64);
  }
  function capTex(lines) {
    return textTex(function (c, w, h) {
      c.fillStyle = '#e8e8e4'; c.strokeStyle = '#e8e8e4';
      c.beginPath(); c.moveTo(w / 2, h * 0.3); c.lineTo(w * 0.62, h * 0.52); c.lineTo(w / 2, h * 0.45); c.lineTo(w * 0.38, h * 0.52); c.closePath(); c.fill();
      c.font = '600 ' + Math.round(h * 0.075) + 'px Arial, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      lines.forEach(function (l, i) { c.fillText(l, w / 2, h * (0.64 + i * 0.1)); });
    }, 256, 256);
  }
  // A knob built along local +x from its seat (x 0); rotate the group to point it up or to a side.
  function knob(parts) {
    var t = new T.Group();
    parts.forEach(function (p) { t.add(p); });
    return t;
  }
  function capFace(r, x, tex, spin) {
    var m = new T.Mesh(new T.CircleGeometry(r, 48), decalMat(tex));
    m.rotation.set(spin || 0, Math.PI / 2, 0, 'YXZ'); m.position.x = x;
    return m;
  }
  function partScope() {
    var g = new T.Group();
    var prof = [[21, 0], [21, 25.2], [17.6, 25.2], [17, 26.4], [17, 29], [17.8, 29.5], [69, 29.5]];
    for (var i = 1; i <= 12; i++) {
      var t = i / 12, s = 0.8 * t + 0.2 * (3 * t * t - 2 * t * t * t);
      prof.push([69 + 47 * t, 29.5 - 12.5 * s]);
    }
    prof.push([269, 17], [272, 20], [274, 21.8], [292, 21.8], [293, 21.75], [345, 21.75], [346, 20.4], [357, 20.4], [357, 18.6], [352, 17.75], [352, 0]);
    g.add(lathe(prof, M.scope, { c: 0.5, seg: 128 }));
    g.add(lens(21.5, 25.2, 2.2, 1, M.glass));
    g.add(lens(352.5, 17.75, 1.4, -1, M.glass));
    g.add(at(knurl(20.6, 10, 72, M.knurl, 0.03), 351.5, 0, 0));
    // magnification ring: raised grip lugs and the 4–16 scale
    for (var k = 0; k < 10; k++) {
      var piv = new T.Group(); piv.rotation.x = k * Math.PI / 5;
      piv.add(at(rbox(9, 2.6, 5.5, 1, M.scope), 279.5, 22.6, 0)); g.add(piv);
    }
    g.add(wrapDecal(21.85, 285, 290.5, 2.2, -0.3, dialTex(['4', '6', '8', '10', '12', '14', '16'], 28)));
    g.add(wrapDecal(29.55, 30, 64, 0.17, 0, sideTex([['ARKEN OPTICS', 600]], 64, 440, 40)));
    g.add(wrapDecal(21.8, 302, 336, 0.42, 0, sideTex([['SH-4 GENII', 600], ['4-16X50', 500]], 128, 470, 44)));
    // saddle and the stud under it
    g.add(at(rbox(44, 46, 46, 14, M.scope), 191, 0, 0));
    var stud = lathe([[0, 0], [0, 4.5], [5, 4], [5, 0]], M.scope); stud.rotation.z = -Math.PI / 2; at(stud, 191, -23, 0); g.add(stud);
    // elevation turret, top at 57.5 above the axis
    var el = knob([
      lathe([[0, 0], [0, 16.5], [6, 16.5], [6, 0]], M.scope),
      lathe([[6, 0], [6, 19.6], [17, 19.6], [17, 0]], M.scope),
      wrapDecal(19.65, 6.5, 16.5, Math.PI * 2, 0, dialTex(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'], 60)),
      at(knurl(20, 15, 30, M.knurl, 0.07), 24.5, 0, 0),
      lathe([[32, 0], [32, 19.2], [36.6, 19], [37.5, 17.8], [37.5, 0]], M.scope),
      capFace(17.6, 37.55, capTex(['ZERO STOP']))
    ]);
    el.rotation.z = Math.PI / 2; at(el, 191, 20, 0); g.add(el);
    // windage turret on the right (-z)
    var wd = knob([
      lathe([[0, 0], [0, 16], [5, 16], [5, 0]], M.scope),
      lathe([[5, 0], [5, 19], [15, 19], [15, 0]], M.scope),
      at(knurl(19.2, 10, 30, M.knurl, 0.07), 20, 0, 0),
      lathe([[25, 0], [25, 18.8], [27, 18.4], [27.5, 17], [27.5, 0]], M.scope),
      capFace(16.8, 27.55, capTex([]))
    ]);
    wd.rotation.y = Math.PI / 2; at(wd, 191, 0, -22.5); g.add(wd);
    // parallax ring with distance scale, illumination knob and slotted cap on the left (+z)
    var px = knob([
      lathe([[0, 0], [0, 17], [3, 17], [3, 0]], M.scope),
      at(knurl(20, 9, 12, M.knurl, 0.05), 7.5, 0, 0),
      wrapDecal(20.05, 4, 11, Math.PI * 2, 0, dialTex(['∞', '1000', '500', '300', '200', '150', '100'], 28)),
      at(knurl(17.5, 11, 30, M.knurl, 0.07), 17.5, 0, 0),
      lathe([[23, 0], [23, 16.5], [28.5, 16], [29.5, 15], [29.5, 0]], M.scope),
      at(rbox(1.2, 1.6, 22, 0.5, M.dark), 29.6, 0, 0)
    ]);
    px.rotation.y = -Math.PI / 2; at(px, 191, 0, 22.5); g.add(px);
    return { group: g };
  }

  function partOcular() {
    var g = new T.Group();
    g.add(lathe([[4, 31.8], [4, 37.5], [8.5, 37.5], [8.5, 35.5], [18, 35.5], [18, 31.8], [4, 31.8]], M.anod, { c: 0.6 }));
    g.add(lathe([[18, 0], [18, 19], [30, 19], [32, 20], [72, 20], [74, 23.5], [124, 23.5], [125.5, 0]], M.anod));
    g.add(lens(19, 17, 1.2, 1, M.glass));
    for (var i = 0; i < 3; i++) {
      var a = -Math.PI / 2 + i * (2 * Math.PI / 3) + 0.4;
      var s = lathe([[0, 0], [0, 2.2], [1.2, 1.8], [1.2, 0]], M.thread);
      s.position.set(3.4, Math.cos(a) * 35.5, Math.sin(a) * 35.5);
      g.add(s);
    }
    var rus = textTex(function (c, w, h) {
      c.save(); c.translate(w / 2, h / 2); c.rotate(-Math.PI / 2);
      c.fillStyle = '#e8e8e4'; c.font = '700 64px Georgia, serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText('RUSAN', 0, 0); c.restore();
    }, 96, 300);
    var dec = wrapDecal(23.55, 84, 118, 0.42, -0.6, rus);
    dec.material.map.repeat.set(1, 1);
    g.add(dec);
    var diop = new T.Group();
    diop.add(lathe([[125.5, 0], [125.5, 21.5], [140, 21.5], [140, 0]], M.anod));
    var marks = textTex(function (c, w, h) {
      c.fillStyle = '#e8e8e4'; c.font = '700 40px Arial, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText('+', w * 0.2, h / 2); c.fillText('0', w * 0.5, h / 2); c.fillText('−', w * 0.8, h / 2);
    }, 200, 56);
    diop.add(wrapDecal(21.55, 129, 136, 0.9, -0.6, marks));
    diop.add(at(knurl(19.5, 11, 48, M.knurl, 0.05), 145.5, 0, 0));
    diop.add(lathe([[151, 0], [151, 17], [152, 16], [152, 0]], M.rubber));
    diop.add(lens(150, 13, 1, -1, M.glass));
    g.add(diop);
    return { group: g, diopter: diop };
  }

  /* ---------- per-sequence choreography (step indexes match the caption list) ---------- */
  function cam(tx, d, az, el) {
    var a = az * Math.PI / 180, e = el * Math.PI / 180;
    return { t: [tx, 0, 0], p: [tx - Math.sin(a) * Math.cos(e) * d, Math.sin(e) * d, Math.cos(a) * Math.cos(e) * d] };
  }
  // Clip-ons the guide can show. L = body length (objective face to the end of the M52 thread at x 0), top = highest point.
  function thunder(o) { return function () { return partThunder(o); }; }
  function hunt(o) { return function () { return partHunt(o); }; }
  function zeiss(o) { return function () { return partZeiss(o); }; }
  var DEV = {
    mate: { make: partMate, name: 'Nocpix MATE', L: 120, top: 46,
      clip: 'Menu \u203a Eyepiece \u203a Clip on \u00b7 then zero in profile 1\u20138', mono: 'Menu \u203a Eyepiece \u203a Monocular' },
    th35c: { make: thunder({ L: 143.4, W: 67.5, objR: 29, objLen: 25, neckR: 24, lensR: 17.5, top: 26.4, bottom: -32, model: 'TH35C 3.0' }),
      name: 'HIKMICRO Thunder TH35C 3.0', L: 143.4, top: 41,
      clip: 'Eyepiece type: Clip-On \u00b7 then Calibrate Image', mono: 'Eyepiece type: Monocular' },
    tq35cl: { make: thunder({ L: 152.3, W: 63, objR: 29, objLen: 25, neckR: 24, lensR: 17.5, top: 27.6, bottom: -32, lrf: true, model: 'TQ35CL 3.0' }),
      name: 'HIKMICRO Thunder TQ35CL 3.0', L: 152.3, top: 42,
      clip: 'Eyepiece type: Clip-On \u00b7 then Calibrate Image', mono: 'Eyepiece type: Monocular' },
    tq50cl: { make: thunder({ L: 150.3, W: 64, objR: 34, objLen: 30, flare: true, neckR: 27, lensR: 25, top: 28.1, bottom: -35.5, lrf: true, model: 'TQ50CL 3.0' }),
      name: 'HIKMICRO Thunder TQ50CL 3.0', L: 150.3, top: 43,
      clip: 'Eyepiece type: Clip-On \u00b7 then Calibrate Image', mono: 'Eyepiece type: Monocular' },
    hunt650: { make: hunt({ L: 124, R: 29.5, objR: 32, lensR: 25, top: 41, foot: 36 }),
      name: 'ThermTec Hunt 650 Pro', L: 124, top: 41,
      clip: 'Menu \u203a Zero Profile \u203a Zeroing \u00b7 shift with + / \u2212', mono: 'Menu \u203a Eyepiece Type \u203a Monocular' },
    hunt335: { make: hunt({ L: 120, R: 28.5, objR: 30, lensR: 17.5, top: 41, foot: 36 }),
      name: 'ThermTec Hunt 335 Pro', L: 120, top: 41,
      clip: 'Menu \u203a Zero Profile \u203a Zeroing \u00b7 shift with + / \u2212', mono: 'Menu \u203a Eyepiece Type \u203a Monocular' },
    hunt650l: { make: hunt({ L: 137, R: 30, objR: 41, lensR: 25, top: 45, foot: 41, lrf: true }),
      name: 'ThermTec Hunt 650L', L: 137, top: 45,
      clip: 'Menu \u203a Zero Profile \u203a Zeroing \u00b7 shift with + / \u2212', mono: 'Menu \u203a Eyepiece Type \u203a Monocular' },
    dtc338: { make: zeiss({ L: 164, R: 28.5, lensR: 19.5, top: 36.5 }),
      name: 'ZEISS DTC 3/38', L: 164, top: 37,
      clip: 'Lock ring turned back against the connector \u00b7 align the image to the reticle', mono: '' }
  };

  function makeSpec(key, devKey) {
    var d = DEV[devKey] || DEV.mate, out = -(d.L + 30), mid = -d.L / 2, lbl = [d.name, mid, d.top + 26];
    if (key === 'clip') return {
      parts: {
        scope: { make: partScope, x: [0, 0, 0, 0, 0, 0, 0], label: ['Arken SH4 4-16x50 GEN2', 191, 64] },
        conn: { make: partConnector, x: [-80, -80, -80, -150, -150, 0, 0], spin: [0, 0, 0, -540, -540, -540, -540], label: ['Rusan MCR-M52 connector', 2, -62] },
        clamp: { make: partClamp, x: [-75, -75, -75, -75, 0, 0, 0], lever: [-14, -14, -14, -14, 0, 0, 0], lock: [LOCK_OPEN, LOCK_OPEN, LOCK_OPEN, LOCK_OPEN, LOCK_OPEN, 0, 0], lockAfter: 'dev', label: ['Rusan MAR clamp \u00b7 \u00d859', 34, 52] },
        dev: { make: d.make, x: [out, out, out, out, out, 0, 0], label: lbl }
      },
      labels: [['scope', 'conn', 'clamp', 'dev'], ['scope'], ['clamp'], ['conn', 'dev'], ['clamp'], [], []],
      notes: ['', 'Zero the day scope first', 'SH4 objective \u00d8 \u224859 mm \u00b7 confirm with calipers', 'Screw on \u00b7 M52\u00d70.75 thread', 'Slide over the objective \u00b7 close the lever', 'Arrows lined up \u00b7 push in \u00b7 turn ring against UNLOCK', d.clip],
      cams: [cam(60 - (d.L - 120) / 2, 1000 + (d.L - 120) * 2, 16, 16), cam(187, 640, 8, 14), cam(35, 430, 50, 14), cam(-175 - (d.L - 120) / 2, 420 + (d.L - 120), -38, 16), cam(30, 470, 24, 22), cam(-5, 440, 32, 24), cam(110, 1020 + (d.L - 120), 20, 15)]
    };
    return {
      parts: {
        conn: { make: partConnector, x: [-60, -130, 0, 0, 0], spin: [0, -540, -540, -540, -540], label: ['Rusan MCR-M52 connector', 2, -62] },
        ocular: { make: partOcular, x: [90, 90, 0, 0, 0], label: ['Rusan MS-OK3X 3\u00d7 ocular', 100, 34] },
        dev: { make: d.make, x: [-130 - (d.L - 120), -130 - (d.L - 120), 0, 0, 0], label: lbl }
      },
      labels: [['conn', 'ocular', 'dev'], ['conn'], ['ocular'], ['dev'], ['ocular']],
      notes: ['', 'Screw on \u00b7 M52\u00d70.75 thread', 'Line up the arrows \u00b7 push in \u00b7 twist to lock (direction to confirm)', d.mono, 'Turn the diopter until the image is sharp'],
      cams: [cam(-30 - (d.L - 120) / 2, 780 + (d.L - 120) * 2, 16, 16), cam(-160 - (d.L - 120), 420 + (d.L - 120), -38, 16), cam(0 - (d.L - 120) / 3, 520 + (d.L - 120), 28, 16), cam(mid + 5, 400 + (d.L - 120), 4, 10), cam(110, 380, -36, 18)]
    };
  }

  function materials() {
    return {
      body: new T.MeshPhysicalMaterial({ color: 0x1b1b1d, metalness: 0.35, roughness: 0.46, clearcoat: 0.25, clearcoatRoughness: 0.6 }),
      anod: new T.MeshPhysicalMaterial({ color: 0x131315, metalness: 0.65, roughness: 0.34, clearcoat: 0.35, clearcoatRoughness: 0.4 }),
      scope: new T.MeshPhysicalMaterial({ color: 0x0c0c0d, metalness: 0.3, roughness: 0.5, clearcoat: 0.15, clearcoatRoughness: 0.6, envMapIntensity: 0.7 }),
      matte: new T.MeshStandardMaterial({ color: 0x18181a, metalness: 0.2, roughness: 0.7 }),
      rubber: new T.MeshStandardMaterial({ color: 0x101011, metalness: 0, roughness: 0.95 }),
      knurl: new T.MeshStandardMaterial({ color: 0x161618, metalness: 0.55, roughness: 0.42, flatShading: true }),
      thread: new T.MeshStandardMaterial({ color: 0x2c2c30, metalness: 0.85, roughness: 0.3 }),
      glass: new T.MeshPhysicalMaterial({ color: 0x060a10, metalness: 0.25, roughness: 0.03, clearcoat: 1, iridescence: 1, iridescenceIOR: 1.8, iridescenceThicknessRange: [280, 520], envMapIntensity: 1.6 }),
      liner: new T.MeshStandardMaterial({ color: 0x23377a, roughness: 0.85 }),
      dark: new T.MeshStandardMaterial({ color: 0x050505, roughness: 0.9 }),
      red: new T.MeshStandardMaterial({ color: 0xc8262c, metalness: 0.3, roughness: 0.4 }),
      orange: new T.MeshStandardMaterial({ color: 0xe0601f, metalness: 0.1, roughness: 0.5 })
    };
  }

  /* ---------- scene ---------- */
  function View(fig, key) {
    var spec = makeSpec(key, fig.getAttribute('data-asm-dev'));
    var stage = fig.querySelector('.asm-stage');
    var wrap = document.createElement('div');
    wrap.className = 'asm-3d';
    wrap.innerHTML = '<canvas aria-hidden="true"></canvas><div class="asm-3d-chip" hidden></div>' +
      (fine ? '<div class="asm-3d-hint">Drag to turn</div>' : '');
    stage.insertBefore(wrap, stage.firstChild);
    var canvas = wrap.querySelector('canvas');
    this.chip = wrap.querySelector('.asm-3d-chip');
    this.wrap = wrap; this.spec = spec; this.stage = stage;

    var r = this.renderer = new T.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    r.outputEncoding = T.sRGBEncoding;
    r.toneMapping = T.ACESFilmicToneMapping;
    r.toneMappingExposure = 1.05;
    r.shadowMap.enabled = true;
    r.shadowMap.type = T.PCFSoftShadowMap;
    r.physicallyCorrectLights = true;

    var scene = this.scene = new T.Scene();
    var pm = new T.PMREMGenerator(r);
    scene.environment = pm.fromScene(new T.RoomEnvironment(), 0.04).texture;
    var key1 = new T.DirectionalLight(0xffffff, 2.2);
    key1.position.set(-250, 520, 380);
    key1.castShadow = true;
    key1.shadow.mapSize.set(2048, 2048);
    var sc = key1.shadow.camera; sc.left = -480; sc.right = 520; sc.top = 260; sc.bottom = -260; sc.near = 10; sc.far = 1600;
    key1.shadow.bias = -0.0004;
    scene.add(key1);
    var rim = new T.DirectionalLight(0xfff1e6, 1.1); rim.position.set(300, 120, -400); scene.add(rim);
    var ground = new T.Mesh(new T.PlaneGeometry(3000, 3000), new T.ShadowMaterial({ opacity: 0.16 }));
    ground.rotation.x = -Math.PI / 2; ground.position.y = -62; ground.receiveShadow = true;
    scene.add(ground);

    this.camera = new T.PerspectiveCamera(26, 16 / 9, 5, 5000);
    this.controls = new T.OrbitControls(this.camera, canvas);
    this.controls.enableZoom = false; this.controls.enablePan = false; this.controls.enableDamping = true;
    this.controls.minPolarAngle = 0.35; this.controls.maxPolarAngle = 1.75;
    this.controls.enabled = fine;
    var self = this;
    this.controls.addEventListener('change', function () { self.kick(); });
    this.controls.addEventListener('start', function () { self.userCam = true; });

    this.parts = {};
    this.labels = {};
    Object.keys(spec.parts).forEach(function (k) {
      var ps = spec.parts[k], built = ps.make();
      scene.add(built.group);
      self.parts[k] = { spec: ps, obj: built, x: ps.x[0], spin: 0, lever: ps.lever ? ps.lever[0] : 0, lock: ps.lock ? ps.lock[0] : 0 };
      var lb = document.createElement('span');
      lb.className = 'asm-3d-lbl'; lb.textContent = ps.label[0]; lb.hidden = true;
      wrap.appendChild(lb);
      self.labels[k] = { el: lb, v: new T.Vector3(ps.label[1], ps.label[2], 0) };
    });
    this.camPos = new T.Vector3(); this.camTgt = new T.Vector3();
    this.goalPos = new T.Vector3(); this.goalTgt = new T.Vector3();
    this.n = 0; this.t0 = 0; this.running = false; this.visible = true;
    this.resize();
    this.setStep(this.currentStep(fig), true);
    this.ready = true;
    if ('ResizeObserver' in window) new ResizeObserver(function () { self.resize(); self.kick(); }).observe(wrap);
    else window.addEventListener('resize', function () { self.resize(); self.kick(); });
    fig.addEventListener('asm:step', function (e) { self.setStep(e.detail.n, reduce); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { self.visible = es[0].isIntersecting; if (self.visible) self.kick(); }).observe(wrap);
    }
    stage.classList.add('is-3d');
  }

  View.prototype.currentStep = function (fig) {
    var items = [].slice.call(fig.querySelectorAll('.asm-steps li'));
    for (var i = 0; i < items.length; i++) if (items[i].getAttribute('aria-current') === 'step') return i;
    return items.length - 1;
  };
  View.prototype.resize = function () {
    var w = this.wrap.clientWidth, h = this.wrap.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    // cams are framed for a 16:9 stage; pull back on taller stages so the same width stays in view
    var fit = Math.round(Math.max(1, (16 / 9) / (w / h)) * 100) / 100, narrow = w < 560;
    if (fit !== this.fit || narrow !== this.narrow) {
      this.fit = fit; this.narrow = narrow;
      if (this.ready) { this.aim(); this.camPos.copy(this.goalPos); this.camTgt.copy(this.goalTgt); this.showLabels(); }
    }
  };
  View.prototype.aim = function () {
    var c = this.spec.cams[this.n];
    this.goalTgt.fromArray(c.t);
    this.goalPos.fromArray(c.p);
    if (this.fit > 1) this.goalPos.sub(this.goalTgt).multiplyScalar(this.fit).add(this.goalTgt);
  };
  View.prototype.showLabels = function () {
    var self = this, show = this.spec.labels[this.n] || [];
    if (this.narrow && show.length > 1) show = [];
    Object.keys(this.labels).forEach(function (k) { self.labels[k].el.hidden = show.indexOf(k) < 0; });
  };
  View.prototype.setStep = function (n, instant) {
    var self = this;
    this.n = n;
    this.userCam = false;
    this.aim();
    if (instant) {
      this.camPos.copy(this.goalPos); this.camTgt.copy(this.goalTgt);
      Object.keys(this.parts).forEach(function (k) {
        var p = self.parts[k];
        p.x = p.spec.x[n];
        if (p.spec.spin) p.spin = p.spec.spin[n];
        if (p.spec.lever) p.lever = p.spec.lever[n];
        if (p.spec.lock) p.lock = p.spec.lock[n];
      });
    }
    var note = this.spec.notes[n] || '';
    this.chip.textContent = note;
    this.chip.hidden = !note;
    this.showLabels();
    this.t0 = performance.now();
    this.kick();
  };
  View.prototype.kick = function () {
    if (this.running || !this.visible) return;
    this.running = true;
    this.last = performance.now();
    var self = this;
    requestAnimationFrame(function f(now) { if (self.frame(now)) requestAnimationFrame(f); else self.running = false; });
  };
  function ease(cur, to, k, dt) { return cur + (to - cur) * (1 - Math.exp(-k * dt)); }
  View.prototype.frame = function (now) {
    var dt = Math.min(0.05, (now - this.last) / 1000), self = this, moving = false, n = this.n;
    this.last = now;
    var t = (now - this.t0) / 1000;
    Object.keys(this.parts).forEach(function (k) {
      var p = self.parts[k], s = p.spec, g = p.obj.group;
      var tx = s.x[n];
      p.x = ease(p.x, tx, 3.6, dt);
      if (s.spin) p.spin = ease(p.spin, s.spin[n], 2.2, dt);
      if (s.lever && Math.abs(p.x - tx) < 2) p.lever = ease(p.lever, s.lever[n], 6, dt);
      // the locking ring turns only once the clip-on is pushed fully home
      var home = !s.lockAfter || Math.abs(self.parts[s.lockAfter].x - self.parts[s.lockAfter].spec.x[n]) < 0.6;
      if (s.lock && (home || s.lock[n] < p.lock)) p.lock = ease(p.lock, s.lock[n], 3, dt);
      if (Math.abs(p.x - tx) > 0.05 || (s.spin && Math.abs(p.spin - s.spin[n]) > 0.1) || (s.lever && Math.abs(p.lever - s.lever[n]) > 0.05) || (s.lock && Math.abs(p.lock - s.lock[n]) > 0.05)) moving = true;
      g.position.x = p.x;
      if (s.spin) g.rotation.x = p.spin * Math.PI / 180;
      if (p.obj.lever) p.obj.lever.rotation.z = p.lever * Math.PI / 180;
      if (p.obj.collar) p.obj.collar.rotation.x = p.lock * Math.PI / 180;
      if (p.obj.diopter) {
        var on = n === 4 && !reduce && t < 4.2;
        p.obj.diopter.rotation.x = on ? Math.sin(t * 2.4) * 0.45 * Math.min(1, t) * (1 - t / 4.2) : 0;
        if (on) moving = true;
      }
    });
    if (!this.userCam) {
      this.camPos.lerp(this.goalPos, 1 - Math.exp(-2.8 * dt));
      this.camTgt.lerp(this.goalTgt, 1 - Math.exp(-2.8 * dt));
      if (this.camPos.distanceTo(this.goalPos) > 0.2 || this.camTgt.distanceTo(this.goalTgt) > 0.2) moving = true;
      this.camera.position.copy(this.camPos);
      this.controls.target.copy(this.camTgt);
    }
    var damp = this.controls.update();
    this.renderer.render(this.scene, this.camera);
    var w = this.wrap.clientWidth, h = this.wrap.clientHeight, v = new T.Vector3();
    Object.keys(this.labels).forEach(function (k) {
      var L = self.labels[k];
      if (L.el.hidden) return;
      v.copy(L.v).applyMatrix4(self.parts[k].obj.group.matrixWorld).project(self.camera);
      L.el.style.transform = 'translate(' + ((v.x + 1) / 2 * w).toFixed(1) + 'px,' + ((1 - v.y) / 2 * h).toFixed(1) + 'px) translate(-50%,-100%)';
    });
    return (moving || damp) && this.visible;
  };

  function initRoot(root) {
    if (root.getAttribute('data-asm-3d') || !hasWebGL()) return;
    root.setAttribute('data-asm-3d', 'loading');
    getLibs().then(function () {
      T = window.THREE;
      if (!M) M = materials();
      // build a view only for the drawing on screen; hidden brands and tabs get theirs when first shown
      var make = function (fig) {
        var key = fig.getAttribute('data-asm-seq');
        if (fig.hidden || fig.asm3d || (key !== 'clip' && key !== 'mono')) return;
        try { fig.asm3d = new View(fig, key); } catch (e) { if (window.console) console.warn('asm-3d', e); }
      };
      [].forEach.call(root.querySelectorAll('.asm-fig'), make);
      root.addEventListener('asm:shown', function (e) { make(e.target); });
      root.setAttribute('data-asm-3d', 'on');
    }).catch(function () { root.setAttribute('data-asm-3d', 'off'); });
  }
  function boot() {
    var roots = [].slice.call(document.querySelectorAll('[data-asm]'));
    if (!roots.length) return;
    if (!('IntersectionObserver' in window)) { roots.forEach(initRoot); return; }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); initRoot(e.target); } });
    }, { rootMargin: '400px 0px' });
    roots.forEach(function (r) { io.observe(r); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
